import { useEffect, useState } from "react"
import { APIS } from "../../../config/apis"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ESTADO_CAJA } from "../../../consts/estadoCaja"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import { ServerRequest } from "../../../utils/apiweb"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import { isValidNumber } from "../../../utils/validator"
import { FormError, FormLabel, InputEntidad, InputLista, InputUsuario, Modal } from "../../common"
import { useForm, useFormErrors, useManagedContext } from "../../hooks"

const entityInit = {
    id: -1,
    codigo: '',
    nombre: '',
    orden: 0,
    idDependencia: 0,
    idEstadoCaja: 0,
    idUsuarioActual: 0,
    idCajaAsignacionActual: 0,
    idRecaudadora: 0,
}

const CajaModal = ({ editItem, editMode, onCancel, onConfirm }) => {
    const { setIsLoading, showMessageModal } = useManagedContext()

    const [entity, setEntity] = useState(entityInit)
    const [formValues, formHandle, _formReset, formSet] = useForm({
        codigo: '',
        nombre: '',
        orden: 0,
        idDependencia: 0,
        idEstadoCaja: 0,
        idRecaudadora: 0,
    })

    useEffect(() => {
        setEntity(editItem)
        formSet({
            codigo: editItem.codigo,
            nombre: editItem.nombre,
            orden: editItem.orden,
            idDependencia:editItem.idDependencia,
            idEstadoCaja: editItem.idEstadoCaja,
            idRecaudadora: editItem.idRecaudadora,
        })
    }, [editItem])

    const estadoCajaFilter = row =>
        editMode === 'add'
            ? row.id === ESTADO_CAJA.INACTIVA
            : row.id === ESTADO_CAJA.CERRADA || 
              row.id === ESTADO_CAJA.INACTIVA ||
              row.id === ESTADO_CAJA.ABIERTA && entity.idEstadoCaja === ESTADO_CAJA.ABIERTA;

    const Guardar = () => {
        setIsLoading(true)

        ServerRequest(
            editMode === 'add' ? REQUEST_METHOD.POST : REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CAJA,
            editMode === 'modify' ? `/${entity.id}` : '',
            { ...entity, ...formValues, orden: parseInt(formValues.orden) ?? 0 },
            res => res.json().then(() => {
                setIsLoading(false)
                onConfirm()
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            error => {
                onRequestError(error)
                setIsLoading(false)
            },
        )
    }
    const Cerrar = () => {
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CAJA,
            `/cierre/${entity.id}`,
            null,
            res => res.json().then(() => {
                setIsLoading(false)
                onConfirm()
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            error => {
                onRequestError(error)
                setIsLoading(false)
            },
        )
    }

    const getErrors = () => {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (!isValidNumber(formValues.orden, true)) errors.orden = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (!isValidNumber(formValues.idDependencia, true)) errors.idDependencia = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (!isValidNumber(formValues.idEstadoCaja, true)) errors.idEstadoCaja = VALIDATION_MESSAGE.REQUIRED_OPTION
        if (!isValidNumber(formValues.idRecaudadora, true)) errors.idRecaudadora = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    const { errorOf, validate } = useFormErrors(getErrors)

    const onClickConfirm = () => {
        if (!validate()) return

        Guardar();
    }
    const onClickCerrar = () => {
        showMessageModal({
            title: "Confirmación",
            message: "¿Está seguro/a de cerrar la caja?",
            onConfirm: () => Cerrar(),
        })
    }

    const disabled = editMode === 'view'
    const isEstadoDisabled = disabled || editMode === 'add' || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA

    return (
        <Modal
            onEscape={onCancel}
            size="lg"
            title="Caja"
            body={(
                <div className='row form-basic'>
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="codigo" required>Código</FormLabel>
                        <input
                            type="text"
                            name="codigo"
                            maxLength={50}
                            className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                            value={formValues.codigo}
                            onChange={formHandle}
                            disabled={disabled || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA}
                        />
                        <FormError message={errorOf('codigo')} />
                    </div>
                    <div className="col-12 col-md-5">
                        <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                        <input
                            type="text"
                            name="nombre"
                            className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                            value={formValues.nombre}
                            onChange={formHandle}
                            disabled={disabled || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA}
                        />
                        <FormError message={errorOf('nombre')} />
                    </div>
                    <div className="col-12 col-md-3">
                        <FormLabel htmlFor="idEstadoCaja" required>Estado</FormLabel>
                        <InputLista
                            name="idEstadoCaja"
                            className={`form-control ${errorOf('idEstadoCaja') ? 'is-invalid' : ''}`}
                            value={formValues.idEstadoCaja}
                            onChange={formHandle}
                            disabled={isEstadoDisabled}
                            lista="EstadoCaja"
                            filter={estadoCajaFilter}
                            withItemZero={false}
                        />
                        <FormError message={errorOf('idEstadoCaja')} />
                    </div>
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="orden" required>Orden</FormLabel>
                        <input
                        name="orden"
                        type="number"
                        placeholder=""
                        className={`form-control ${errorOf('orden') ? 'is-invalid' : ''}`}
                        value={formValues.orden}
                        onChange={formHandle}
                        disabled={disabled || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA}
                        />
                        <FormError message={errorOf('orden')} />
                    </div>

                    <div className="col-12 col-md-7">
                        <FormLabel htmlFor="idDependencia" required>Dependencia</FormLabel>
                        <InputEntidad
                            name="idDependencia"
                            placeholder=""
                            className={`form-control ${errorOf('idDependencia') ? 'is-invalid' : ''}`}
                            value={formValues.idDependencia}
                            onChange={formHandle}
                            disabled={disabled || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA}
                            entidad="Dependencia"
                            title="Dependencia"
                        />
                        <FormError message={errorOf('idDependencia')} />
                    </div>
                    <div className="col-12 col-md-5">
                        <FormLabel htmlFor="idRecaudadora" required>Recaudadora</FormLabel>
                        <InputEntidad
                            name="idRecaudadora"
                            placeholder=""
                            className={`form-control ${errorOf('idRecaudadora') ? 'is-invalid' : ''}`}
                            value={formValues.idRecaudadora}
                            onChange={formHandle}
                            disabled={disabled || entity.idEstadoCaja === ESTADO_CAJA.ABIERTA}
                            entidad="Recaudadora"
                            title="Recaudadora"
                        />
                        <FormError message={errorOf('idRecaudadora')} />
                    </div>
                    {entity.idEstadoCaja === ESTADO_CAJA.ABIERTA &&
                    <div className="col-12">
                        <label htmlFor="idUsuarioActual" className="form-label">Usuario</label>
                        <InputUsuario
                            name="idUsuarioActual"
                            placeholder=""
                            className="form-control"
                            value={entity.idUsuarioActual}
                            disabled={true}
                        />
                        <FormError />
                    </div>
                    }

                </div>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={onCancel}>Volver</button>
                    {editMode !== 'view' && entity.idEstadoCaja !== ESTADO_CAJA.ABIERTA && (
                        <button className="btn action-button" data-dismiss="modal" onClick={onClickConfirm}>Guardar</button>
                    )}
                    {editMode === 'modify' && entity.idEstadoCaja === ESTADO_CAJA.ABIERTA && (
                        <button className="btn action-button" data-dismiss="modal" onClick={onClickCerrar}>Cerrar</button>
                    )}
                </div>
                
            )}
        />
    )
}

export default CajaModal
